import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  User,
  Clock,
  ChevronRight,
  Sparkles,
  Share2,
  Facebook,
  Linkedin,
  Link as LinkIcon,
} from "lucide-react";
import microplasticImg from "../assets/images/microplastic.png";
import edibleCupImg from "../assets/images/edible_cups.jpg";
import plasticDetoxImg from "../assets/images/plastic_detox.jpg";

// X (formerly Twitter) icon
const XIcon = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const SharePostButtons = ({ title, slug }) => {
  const [copied, setCopied] = useState(false);
  const baseUrl =
    typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${baseUrl}/blog${slug ? `#${slug}` : ""}`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const shareText = encodeURIComponent(title);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (_) {}
      document.body.removeChild(textarea);
    }
  };

  return (
    <div className="single-post-share inline-share">
      <span className="share-label">
        <Share2 size={16} /> Share this article
      </span>
      <div className="share-buttons">
        <a
          className="share-btn share-facebook"
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Facebook"
        >
          <Facebook size={18} />
        </a>
        <a
          className="share-btn share-x"
          href={`https://x.com/intent/post?text=${shareText}&url=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X"
        >
          <XIcon size={16} />
        </a>
        <a
          className="share-btn share-linkedin"
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
        >
          <Linkedin size={18} />
        </a>
        <a
          className="share-btn share-whatsapp"
          href={`https://api.whatsapp.com/send?text=${shareText}%20${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on WhatsApp"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04 0C5.46 0 .12 5.34.12 11.92c0 2.1.55 4.16 1.6 5.97L0 24l6.27-1.65a11.9 11.9 0 0 0 5.77 1.47h.01c6.58 0 11.92-5.34 11.92-11.92 0-3.18-1.24-6.17-3.45-8.42zM12.05 21.6h-.01a9.66 9.66 0 0 1-4.93-1.35l-.35-.21-3.72.98.99-3.63-.23-.37a9.65 9.65 0 0 1-1.49-5.1c0-5.34 4.35-9.69 9.7-9.69 2.59 0 5.02 1.01 6.85 2.84a9.6 9.6 0 0 1 2.84 6.85c0 5.34-4.35 9.68-9.65 9.68zm5.31-7.25c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.15-.66.15-.2.29-.76.95-.93 1.15-.17.2-.34.22-.63.07-.29-.15-1.22-.45-2.33-1.43-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.49-.66-.5l-.56-.01c-.2 0-.51.07-.78.37-.27.29-1.03 1.01-1.03 2.46 0 1.45 1.06 2.85 1.21 3.05.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.08 1.72-.7 1.96-1.38.24-.68.24-1.27.17-1.38-.07-.12-.27-.2-.56-.34z" />
          </svg>
        </a>
        <button
          type="button"
          className={`share-btn share-copy ${copied ? "copied" : ""}`}
          onClick={handleCopy}
          aria-label="Copy link"
        >
          <LinkIcon size={18} />
          <span className="copy-tooltip">{copied ? "Copied!" : "Copy"}</span>
        </button>
      </div>
    </div>
  );
};

const Blog = () => {
  const [isCollaborationVisible, setIsCollaborationVisible] = useState(false);
  const [expandedPostId, setExpandedPostId] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById("collaboration-section");
      if (section) {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          setIsCollaborationVisible(true);
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleExpand = (postId) => {
    setExpandedPostId(expandedPostId === postId ? null : postId);
  };

  const blogPosts = [
    {
      id: 3,
      title: "Plastic Detox: Rethinking Everyday Waste",
      excerpt:
        "The Netflix documentary The Plastic Detox shows how single-use plastic harms our planet and how reusable, biodegradable, and edible alternatives are reshaping the future of packaging.",
      category: "Sustainability",
      author: "Magomeal Team",
      date: "May 8, 2026",
      readTime: "5 min read",
      featured: true,
      external: true,
      link: "/blog/plastic-detox-rethinking-everyday-waste",
      image: plasticDetoxImg,
    },
    {
      id: 1,
      title:
        "Microplastics in Disposable Paper Cups: Hidden Risks in Hot Drinks",
      excerpt:
        "Research shows that disposable paper cups shed microplastics and release harmful chemicals when exposed to hot liquids, posing potential health risks.",
      category: "Technology",
      author: "Magomeal Team",
      date: "November 23, 2025",
      readTime: "5 min read",
      featured: true,
      content: (
        <div>
          <p>
            Most disposable paper cups aren’t just paper. To prevent leaks, their
            interiors are coated with a thin plastic lining. Studies show that
            when hot drinks are poured into these cups, the lining can release
            tens of thousands of microplastic particles along with chemicals like
            fluoride, chloride, and even trace metals such as lead, chromium, and
            cadmium.
          </p>

          <img
            src={microplasticImg}
            alt="Microplastics in paper cup"
            style={{
              width: "100%",
              margin: "1rem 0",
              borderRadius: "8px",
            }}
          />

          <p>
            These microplastics and chemicals are invisible to the eye but
            represent real exposure every time you drink hot beverages. The
            long-term health effects are still under research, but evidence
            suggests potential risks including inflammation, cellular stress,
            and accumulation in body tissues.
          </p>

          <p>
            Choosing reusable cups or edible alternatives reduces exposure to
            these contaminants and supports sustainability.
          </p>

          <SharePostButtons
            title="Microplastics in Disposable Paper Cups: Hidden Risks in Hot Drinks"
            slug="microplastics-disposable-paper-cups"
          />
        </div>
      ),
    },

    {
      id: 2,
      title: "Are Edible Cups Safe? What They’re Made Of and Why They Matter",
      excerpt:
        "Edible cups are emerging as a practical alternative to plastic-lined paper cups. This article explains what they are made of, how they perform with hot drinks, and why they matter.",
      category: "Technology",
      author: "Magomeal Team",
      date: "December 29, 2025",
      readTime: "4 min read",
      featured: true,
      content: (
        <div>
          <p>
            As awareness grows around microplastics and chemical leaching from
            disposable paper cups, edible cups are gaining attention as a safer
            alternative. A natural question follows: are edible cups actually
            safe?
          </p>

          <img
            src={edibleCupImg}
            alt="Edible cup for hot drinks"
            style={{
              width: "100%",
              margin: "1rem 0",
              borderRadius: "8px",
            }}
          />

          <p>
            Edible cups are made from food-grade ingredients such as grains,
            bran, and plant-based oils. They contain no plastic linings or
            synthetic coatings. Everything touching the drink is designed to be
            consumed.
          </p>

          <p>
            Properly designed edible cups can hold hot beverages like coffee or
            tea for the full drinking experience without collapsing or altering
            the drink. With no plastic barrier involved, the risk of
            microplastic release is eliminated.
          </p>

          <SharePostButtons
            title="Are Edible Cups Safe? What They’re Made Of and Why They Matter"
            slug="are-edible-cups-safe"
          />
        </div>
      ),
    },
  ];

  const categories = ["All", "Innovation", "Business", "Technology"];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredPosts =
    selectedCategory === "All"
      ? blogPosts
      : blogPosts.filter((post) => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <h1 className="display-medium">Blog & News</h1>
          <p className="body-large">
            Stay updated with the latest in edible packaging innovations and
            sustainable business practices.
          </p>
        </div>
      </section>
	  
	        {/* Museum Collaboration Section */}
      <section id="collaboration-section" className={`collaboration-section ${isCollaborationVisible ? 'visible' : ''}`}>
        <div className="container">
          <div className="collaboration-banner">
            <Sparkles className="banner-icon" size={24} />
            <span>Sustainability in Action</span>
          </div>
          
          <div className="collaboration-content">
            <div className="collaboration-text">
              <h2 className="collaboration-title">Magomeal × Home Computer Museum Helmond</h2>
              <p className="collaboration-description">
                We are happy to share that Magomeal is now collaborating with the Home Computer Museum Helmond.
              </p>
              <p className="collaboration-description">
                The museum has chosen to replace paper cups with our edible cups, helping reduce waste in a simple and fun way. Visitors can enjoy their drink and then eat the cup.
              </p>
              <p className="collaboration-description">
                We're proud to be part of this step toward more sustainable everyday choices and excited to work with a place that celebrates innovation.
              </p>
            </div>
            
            <div className="collaboration-logo">
              <a 
                href="https://www.homecomputermuseum.nl/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="museum-logo-link"
              >
                <img 
                  src={require('../assets/images/hcm_logo.png')} 
                  alt="Home Computer Museum Helmond Logo" 
                  className="museum-logo"
                />
                <span className="visit-museum">Visit Museum Website →</span>
              </a>
            </div>
          </div>
        </div>
      </section>
	  

      {/* Featured Post */}
{featuredPosts.length > 0 && (
  <section className="featured-post-section">
    <div className="container">
      {featuredPosts.map(post => (
        <div key={post.id} className="featured-post network-card">
          <div className="featured-badge">Featured Article</div>

          <div className="featured-content">
            <div className="post-category">{post.category}</div>
            <h2 className="heading-1">{post.title}</h2>
            <p className="body-large">{post.excerpt}</p>

            {!post.external && expandedPostId === post.id && (
              <div className="post-content body-large">
                {post.content}
              </div>
            )}

            <div className="post-meta">
              <div className="meta-item">
                <User size={16} />
                <span className="body-small">{post.author}</span>
              </div>
              <div className="meta-item">
                <Calendar size={16} />
                <span className="body-small">{post.date}</span>
              </div>
              <div className="meta-item">
                <Clock size={16} />
                <span className="body-small">{post.readTime}</span>
              </div>
            </div>

            {post.external ? (
              <Link to={post.link} className="btn-primary">
                Read Full Article <ChevronRight size={20} />
              </Link>
            ) : (
              <button
                className="btn-primary"
                onClick={() => toggleExpand(post.id)}
              >
                {expandedPostId === post.id
                  ? "Collapse Article"
                  : "Read Full Article"}{" "}
                <ChevronRight size={20} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  </section>
)}


      {/* Blog Grid */}
      <section className="blog-posts-section">
        <div className="container">
          <div className="blog-posts-grid">
            {filteredPosts
              .filter((post) => !post.featured)
              .map((post) => (
                <article
                  key={post.id}
                  className="blog-post-card network-card hover-lift"
                >
                  <div className="post-header">
                    <div className="post-category">{post.category}</div>
                    <h3 className="network-card-title">{post.title}</h3>
                  </div>

                  <p className="network-card-content">{post.excerpt}</p>

                  {expandedPostId === post.id && (
                    <div className="post-content body-large">
                      {post.content}
                    </div>
                  )}

                  <div className="post-meta">
                    <div className="meta-item">
                      <User size={14} />
                      <span className="body-small">{post.author}</span>
                    </div>
                    <div className="meta-item">
                      <Calendar size={14} />
                      <span className="body-small">{post.date}</span>
                    </div>
                    <div className="meta-item">
                      <Clock size={14} />
                      <span className="body-small">
                        {post.readTime}
                      </span>
                    </div>
                  </div>

                  <div className="post-footer">
                    <button
                      className="read-more-btn"
                      onClick={() => toggleExpand(post.id)}
                    >
                      {expandedPostId === post.id
                        ? "Collapse Article"
                        : "Read Full Article"}{" "}
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </article>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
